
import React, { useEffect } from 'react';
import { X, Gift, Globe, Lightbulb, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export const MerchandisingModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const features = [
    {
      icon: Globe,
      title: 'Global Concept Sourcing',
      description: 'Access to innovative merchandise concepts from around the world, ensuring your promotional products stand out and make lasting impressions.',
      details: [
        'International trend research and analysis',
        'Innovative product concept discovery',
        'Quality supplier network access',
        'Cultural adaptation and localization'
      ]
    },
    {
      icon: Lightbulb,
      title: 'Creative Product Development',
      description: 'Creative development process transforms your brand vision into tangible promotional products that ensure maximum impact and memorability.',
      details: [
        'Brand-aligned product conceptualization',
        'Design iteration and refinement',
        'Prototype development and testing',
        'Production readiness optimization'
      ]
    },
    {
      icon: TrendingUp,
      title: 'Brand Positioning Enhancement',
      description: 'Strategic merchandise selection and customization that reinforces your brand positioning and creates positive brand associations in the minds of recipients.',
      details: [
        'Brand positioning analysis',
        'Strategic product selection criteria',
        'Brand message integration',
        'Consistency across all touchpoints'
      ]
    },
    {
      icon: Gift,
      title: 'Market Growth Strategies',
      description: 'Leverage promotional products and corporate gifts as powerful marketing tools to drive customer acquisition, retention, and market expansion.',
      details: [
        'Customer journey mapping',
        'Strategic gifting campaign development',
        'Market penetration strategies',
        'Customer lifetime value optimization'
      ]
    }
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-1 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg sm:rounded-3xl max-w-5xl max-h-[98vh] sm:max-h-[90vh] w-full overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="bg-gradient-to-r from-amber-700 to-orange-600 p-2 sm:p-6 text-white relative flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-1 right-1 sm:top-4 sm:right-4 p-1 sm:p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-4 h-4 sm:w-6 sm:h-6" />
          </button>
          <div className="flex items-center gap-1 sm:gap-4 pr-6 sm:pr-12">
            <Gift className="w-4 h-4 sm:w-10 sm:h-10 text-white flex-shrink-0" style={{strokeWidth: '1.5', fill: 'none'}} />
            <div className="min-w-0">
              <h2 className="text-sm sm:text-3xl font-bold leading-tight">Merchandising & Corporate Gifting</h2>
              <p className="text-white/90 text-xs sm:text-lg mt-0 sm:mt-1 hidden sm:block">Brand Enhancement Through Strategic Products</p>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-2 sm:p-6">
          <div className="mb-3 sm:mb-6">
            <p className="text-gray-700 text-sm sm:text-lg leading-relaxed">
              We create merchandise people actually want to keep. Quality items that represent your brand well and leave a lasting impression on everyone who receives them.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl sm:rounded-2xl p-3 sm:p-6 border border-orange-100">
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-4">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center">
                    <feature.icon className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" style={{strokeWidth: '1.5', fill: 'none'}} />
                  </div>
                  <h3 className="text-sm sm:text-xl font-bold text-gray-900">{feature.title}</h3>
                </div>

                <p className="text-gray-700 mb-2 sm:mb-4 leading-relaxed text-xs sm:text-base">{feature.description}</p>

                <ul className="space-y-1 sm:space-y-2">
                  {feature.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                      <ArrowRight className="w-2 h-2 sm:w-3 sm:h-3 text-orange-600 flex-shrink-0" style={{strokeWidth: '1.5', fill: 'none'}} />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-4 sm:mt-8 p-3 sm:p-6 bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl sm:rounded-2xl text-white text-center">
            <h4 className="text-sm sm:text-xl font-bold mb-1 sm:mb-2">Ready to Make a Lasting Impression?</h4>
            <p className="text-gray-300 mb-2 sm:mb-4 text-xs sm:text-base">Let's create promotional products that truly represent your brand</p>
            <Button
              onClick={onClose}
              className="bg-gradient-to-r from-amber-700 to-orange-600 text-white hover:opacity-90 transition-opacity px-4 sm:px-8 text-xs sm:text-base"
            >
              Get Started
              <ArrowRight className="ml-1 sm:ml-2 w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
